<script setup lang="ts">
  import { ref, onMounted, watch } from 'vue';
  import { DatePicker, SelectInput, IconImage, FilterModal, ThemeProvider } from '@/shared/ui';
  import ArrowRight from '@/shared/assets/icons/ArrowRight.svg';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { STATUS_OPTIONS } from '@/entities/Document';
  import type { FilterOptions } from '@/entities/Document';
  import { formatCivilDateToPtBR, parseString, parseDate, parseNumber } from '@/shared/lib';

  const props = defineProps<{
    currentFilters?: FilterOptions;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: FilterOptions];
    'clear-filters': [];
  }>();

  const formData = ref({
    hasTraining: '',
    status: '',
    effectiveDate: null as Date | null,
    revision: 0,
  });

  const isEffectiveDateOpen = ref(false);

  function loadFilters(filters?: FilterOptions) {
    if (!filters) return;

    formData.value.hasTraining =
      filters.hasTraining !== undefined && filters.hasTraining !== null
        ? String(filters.hasTraining)
        : '';
    formData.value.status = parseString(filters.status);
    formData.value.effectiveDate = parseDate(filters.effectiveDate);
    formData.value.revision = parseNumber(filters.revision);
  }

  onMounted(() => {
    loadFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (newFilters?: FilterOptions) => {
      if (newFilters) {
        loadFilters(newFilters);
      }
    },
    { deep: true }
  );

  const handleApplyFilters = () => {
    const filters: any = {};
    if (formData.value.hasTraining) filters.hasTraining = formData.value.hasTraining === 'true';
    if (formData.value.status) filters.status = formData.value.status;
    if (formData.value.effectiveDate) filters.effectiveDate = formData.value.effectiveDate;
    if (formData.value.revision > 0) filters.revision = formData.value.revision;

    emit('update:filter', filters);
    emit('close');
  };

  const handleClearFilters = () => {
    Object.assign(formData.value, {
      hasTraining: '',
      status: '',
      effectiveDate: null,
      revision: 0,
    });
    emit('clear-filters');
  };

  const selectTraining = (training: string) => {
    formData.value.hasTraining = formData.value.hasTraining === training ? '' : training;
  };

  const incrementRevision = () => {
    formData.value.revision++;
  };

  const decrementRevision = () => {
    if (formData.value.revision > 0) {
      formData.value.revision--;
    }
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-md"
    @close="emit('close')"
    @apply-filters="handleApplyFilters"
    @clear-filters="handleClearFilters"
  >
    <template #content>
      <ThemeProvider>
        <div class="p-3">
          <div class="py-3 px-6">
            <h4 class="font-medium text-gray-07 pb-4">
              Treinamento
            </h4>
            <div class="flex flex-col gap-2">
              <button
                :class="[
                  'py-2 px-4 rounded-lg text-left transition-colors',
                  formData.hasTraining === 'true'
                    ? 'bg-[var(--theme-secondary)] text-white'
                    : 'text-gray-07 hover:bg-gray-100',
                ]"
                @click="selectTraining('true')"
              >
                Com treinamento
              </button>
              <button
                :class="[
                  'py-2 px-4 rounded-lg text-left transition-colors',
                  formData.hasTraining === 'false'
                    ? 'bg-[var(--theme-secondary)] text-white'
                    : 'text-gray-07 hover:bg-gray-100',
                ]"
                @click="selectTraining('false')"
              >
                Sem treinamento
              </button>
            </div>
          </div>

          <div class="p-6 border-y border-gray-02">
            <h4 class="font-medium text-gray-07 pb-4">
              Status
            </h4>
            <SelectInput
              :model-value="formData.status"
              :options="STATUS_OPTIONS"
              label="Selecione um Status"
              @update:model-value="formData.status = $event"
            />
          </div>

          <div class="p-6">
            <h4 class="font-medium text-gray-07 pb-4">
              Data ou período
            </h4>
            <button
              class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-[var(--theme-secondary-ink)] font-medium w-full"
              @click="isEffectiveDateOpen = true"
            >
              <IconImage
                :src="CalendarIcon"
                alt="Calendário"
                class="h-5 w-5 mr-2"
              />
              <span class="flex-grow text-left">{{
                formData.effectiveDate !== null
                  ? formatCivilDateToPtBR(formData.effectiveDate)
                  : 'Selecione uma data'
              }}</span>
            </button>
            <DatePicker
              v-model:selected-date="formData.effectiveDate"
              v-model:is-open="isEffectiveDateOpen"
              class="my-auto"
              @apply="isEffectiveDateOpen = false"
              @cancel="isEffectiveDateOpen = false"
            />
          </div>

          <div class="p-6 border-t border-gray-02">
            <h4 class="font-medium text-gray-07 pb-4">
              Revisões
            </h4>
            <div class="relative w-32">
              <input
                v-model.number="formData.revision"
                type="number"
                min="0"
                class="w-full px-3 py-2 pr-8 border border-gray-02 rounded-lg focus:outline-none focus:border-[var(--theme-secondary-ink)] [appearance:textfield] [-webkit-appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0"
              >
              <div class="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col gap-0">
                <IconImage
                  :src="ArrowRight"
                  alt="Aumentar"
                  class="h-6 w-6 rotate-[270deg] cursor-pointer -mb-3 -ml-[1.52px]"
                  @click="incrementRevision"
                />
                <IconImage
                  :src="ArrowRight"
                  alt="Diminuir"
                  class="h-6 w-6 rotate-90 cursor-pointer"
                  @click="decrementRevision"
                />
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
